import type { GeometryPadWidgetConfig } from '../geometrypad.model';

// All presets use the same SAS triangle to demonstrate each label style.
const BASE_TRIANGLE = {
  type: 'Triangle' as const,
  params: { a: 5, b: 7, gamma: 50 },
};

/**
 * Given-only: only the given (input) elements are labeled with name + value.
 * Non-given (derived) elements have no labels.
 *
 * Use for: problem statements where only the knowns should be visible.
 */
export const LABEL_GIVEN_ONLY_PRESET: GeometryPadWidgetConfig = {
  id: 'label-given-only',
  type: 'geometrypad',
  params: {
    elements: { t1: BASE_TRIANGLE },
  },
  display: {
    geometry: {
      label: { names: 'given', values: 'given', coloring: true },
    },
  },
};

/**
 * Worked-example: given elements get name + value, derived elements get name only.
 * This visually separates "what's known" from "what needs to be found".
 *
 * Use for: step-by-step worked examples where derived sides are referenced by name.
 */
export const LABEL_WORKED_EXAMPLE_PRESET: GeometryPadWidgetConfig = {
  id: 'label-worked-example',
  type: 'geometrypad',
  params: {
    elements: { t1: BASE_TRIANGLE },
  },
  display: {
    geometry: {
      label: { names: 'tiered', values: 'given', coloring: true },
    },
  },
};

/**
 * Full-solution: ALL sides and angles labeled with name + value.
 *
 * Use for: final answer diagrams or solution reveals in worked examples.
 */
export const LABEL_FULL_SOLUTION_PRESET: GeometryPadWidgetConfig = {
  id: 'label-full-solution',
  type: 'geometrypad',
  params: {
    elements: { t1: BASE_TRIANGLE },
  },
  display: {
    geometry: {
      label: { names: 'tiered', values: 'tiered', coloring: true },
    },
  },
};

/**
 * Symbolic: ALL sides and angles labeled with names only (no numeric values).
 *
 * Use for: algebraic / proof-style diagrams where variables are referenced symbolically.
 */
export const LABEL_SYMBOLIC_PRESET: GeometryPadWidgetConfig = {
  id: 'label-symbolic',
  type: 'geometrypad',
  params: {
    elements: { t1: BASE_TRIANGLE },
  },
  display: {
    geometry: {
      label: { names: 'tiered', coloring: true },
    },
  },
};

/**
 * Unlabeled: no auto-labels at all. Just the shape with angle arcs.
 *
 * Use for: introductory diagrams, illustrations, or when labels are added manually.
 */
export const LABEL_UNLABELED_PRESET: GeometryPadWidgetConfig = {
  id: 'label-unlabeled',
  type: 'geometrypad',
  params: {
    elements: { t1: BASE_TRIANGLE },
  },
  display: {
    geometry: {
      label: { coloring: true },
    },
  },
};
